// web/src/player/doodle.ts
// Port of RoastFlowCoordinator.findDoodleRevealTime (Swift → TypeScript).
// iOS source: mrroast-ios/Mr Roast/Mr Roast/Coordinators/RoastFlowCoordinator.swift L1613-L1677
import type { WordTiming } from "../types/roast";

// Exact trigger phrases (iOS doodleTriggerPhrases — verbatim list).
const DOODLE_TRIGGER_PHRASES: readonly string[] = [
  "let me fix", "let me improve", "let me draw",
  "I'll make this better", "needs my artistic",
  "here's my version", "my masterpiece", "hold on",
  "wait let me", "one more thing", "almost forgot",
  "let me help", "I can fix", "allow me to",
  "lemme fix", "lemme draw", "I'll fix",
];

// Single-word keywords searched in the last 20% of the timeline
// (iOS doodleKeywords — verbatim list).
const DOODLE_KEYWORDS: readonly string[] = [
  "fix", "improve", "draw", "pen", "sketch", "doodle",
  "artistic", "masterpiece", "upgrade", "enhance", "edit", "touch",
];

/**
 * Decide WHEN (in seconds) to reveal the doodle overlay during playback.
 *
 * Rule (faithful TypeScript port of iOS `findDoodleRevealTime`):
 *   Pass 1 — Scan all word timings for exact multi-word trigger phrases
 *             (case-insensitive substring match on each word).
 *   Pass 2 — Scan the last 20% of words for single-word doodle keywords.
 *   Pass 3 — Fall back to `max(0, lastWord.end - 1.5)` seconds.
 *
 * Returns `null` for an empty `timings` array.
 */
export function doodleRevealTime(timings: WordTiming[]): number | null {
  if (timings.length === 0) return null;

  // ── Pass 1: exact trigger-phrase matching ─────────────────────────────────
  for (let i = 0; i < timings.length; i++) {
    const wordLow = timings[i].word.toLowerCase();
    for (const trigger of DOODLE_TRIGGER_PHRASES) {
      const triggerWords = trigger.split(" ");
      // Quick first-word check to avoid inner loop on every miss.
      if (!wordLow.includes(triggerWords[0].toLowerCase())) continue;
      let matches = true;
      for (let offset = 0; offset < triggerWords.length; offset++) {
        if (i + offset < timings.length) {
          if (
            !timings[i + offset].word
              .toLowerCase()
              .includes(triggerWords[offset].toLowerCase())
          ) {
            matches = false;
            break;
          }
        } else {
          matches = false;
          break;
        }
      }
      if (matches) return timings[i].start;
    }
  }

  // ── Pass 2: keyword scan in last 20% ─────────────────────────────────────
  const lastPortionStart = Math.floor(timings.length * 0.8);
  for (let i = lastPortionStart; i < timings.length; i++) {
    const word = timings[i].word.toLowerCase();
    for (const keyword of DOODLE_KEYWORDS) {
      if (word.includes(keyword)) return timings[i].start;
    }
  }

  // ── Pass 3: 1.5 s before last word ends ──────────────────────────────────
  return Math.max(0, timings[timings.length - 1].end - 1.5);
}
